import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api from "api/api";

// props validation
import PropTypes from "prop-types";

// ag grid
import { AgGridReact } from "ag-grid-react";

// redux
import { useSelector, useDispatch } from "react-redux";
import { loadLookupProductsState, loadLookupSelectedProductsState, loadParentIdsState, loadCompareEnabledState} from "store/features/matchNetSlice";
import { showNotification } from "store/features/notificationSlice";

// mui components
import Tooltip from "@mui/material/Tooltip";

// common components
import Loader from "components/common/Loader";

const MatchNetLookupTable = () => {
    const dispatch = useDispatch();
    const gridRef = useRef();

    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [parentRowIds, setParentRowIds] = useState([]);
    const [rowsToListInGrid, setRowsToListInGrid] = useState([]);
    const [selectedProductRows, setSelectedProductRows] = useState([]);
    const [checkboxSelectionIds, setCheckboxSelectionIds] = useState([]);

    const algorithm = useSelector((state) => state.matchNetStore.selectedStudyType);
    const studyPageFilters = useSelector((state) => state.studyDataStore.studyPageFilters);
    const matchNetSelectedProducts = useSelector((state) => state.matchNetStore.selectedProducts);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    const SeeMoreRenderer = ({ value }) => {
        const text = typeof value === "object" && value !== null ? value.value || "" : String(value || "");

        const items = text
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);

        const showSeeMore = items.length > 2;

        const displayText = showSeeMore ? items.slice(0, 2).join(", ") : text;

        return (
            <Tooltip
                title={text}
                placement="bottom"
                slotProps={{
                    tooltip: {
                        sx: {
                            bgcolor: "#fff",
                            color: "#333",
                            boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
                            border: "1px solid #e0e0e0",
                            fontSize: "13px",
                            maxWidth: 400,
                            p: 1.5,
                        },
                    },
                    arrow: {
                        sx: {
                            color: "#fff",
                        },
                    },
                }}
            >
                <span>
                    {displayText}
                    {showSeeMore && (
                        <span
                            style={{
                                color: "#005eef",
                                cursor: "pointer",
                                marginLeft: "4px",
                            }}
                        >
                            see more...
                        </span>
                    )}
                </span>
            </Tooltip>
        );
    };

    SeeMoreRenderer.propTypes = {
        value: PropTypes.string,
    };

    const parentChildCellRenderer = (params) => {
        if (typeof params?.value === "object") {
            const obj = params?.value;
            return obj?.value || "";
        } else {
            return params.value;
        }
    };

    const returnCellStyle = (value, isApplicable) => {
        if (!isApplicable) {
            return {};
        }

        if (value >= 0 && value < 1) {
            return {
                backgroundColor: "red",
                color: "#000000",
            };
        }

        if (value === 1) {
            return {
                backgroundColor: "green",
                color: "#000000",
            };
        }

        return {};
    };

    const getMetadataCellStyle = (params) => {
        if (params.data?.isParentRow) {
            return {};
        }

        const value = typeof params.value === "object" ? params.value?.similarity_score : "";

        const isApplicable = typeof params.value === "object" ? params.value?.is_applicable : false;

        return returnCellStyle(value, isApplicable);
    };

    // Default columns
    const columns = [
        {
            headerName: "Product Match",
            field: "title",
            checkboxSelection: (params) => {
                const isParentRow = params.data.isParentRow;
                return isParentRow ? checkboxSelectionIds.includes(params.data.id) : checkboxSelectionIds.includes(`${params.data.parentId}-${params.data.id}`);
            },
        },
        {
            headerName: "Study Period",
            field: "study_period",
        },
        {
            headerName: "Brand",
            field: "brand",
        },
        {
            headerName: "Product Format",
            field: "product_format",
            cellRenderer: parentChildCellRenderer,
        },
        {
            headerName: "Country",
            field: "country",
        },
        {
            headerName: "Study Type Method",
            field: "study_type_method",
        },
        {
            headerName: "Study Reference",
            field: "study_reference",
        },
        {
            headerName: "Research Design Type",
            field: "research_design_type",
        },
        {
            headerName: "MatchNet Outcome",
            field: "overall_score",
            cellRenderer: (params) => {
                return params.value ?? "-";
            },
        },
        {
            headerName: "Product Identifier LIMS",
            field: "product_identifier_lims",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "Chassis",
            field: "chassis",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "Fragrance",
            field: "fragrance",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "Fragrance Level",
            field: "fragrance_level",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "Antiperspirant Active",
            field: "antiperspirant_active",
            hide: algorithm !== "home_use_test",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "Antiperspirant Active Level",
            field: "antiperspirant_active_level",
            hide: algorithm !== "home_use_test",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "Packaging Body Common Name",
            field: "packaging_body_common_name",
            hide: algorithm !== "home_use_test",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "Format",
            field: "format",
            hide: algorithm !== "home_use_test",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "Ingredients List",
            field: "ingredients_list",
            cellRenderer: SeeMoreRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "PC Family Primary Descriptor",
            field: "pc_family_primary_descriptor",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "PC Family Secondary Descriptor",
            field: "pc_family_secondary_descriptor",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
        {
            headerName: "PC Family Identifier  Descriptor",
            field: "pc_family_identifier_descriptor",
            cellRenderer: parentChildCellRenderer,
            cellStyle: getMetadataCellStyle,
        },
    ];

    useEffect(() => {
        const filteredRowsIds = rowData?.map((row) => row.parent.id);
        const modifiedRows = rowData?.map((item) => {
            const childrenRows = item.children.map((child, index) => ({
                ...child,
                parentId: item.parent.id,
                isParentRow: false,
                isGroupEnd: index === item.children.length - 1,
            }));
            return {
                parent: { ...item.parent, isParentRow: true, isGroupEnd: childrenRows.length === 0 },
                children: childrenRows,
            };
        });
        
        const filteredRows = modifiedRows?.flatMap((item) => [item.parent, ...item.children]);
        setCheckboxSelectionIds(filteredRowsIds);
        setParentRowIds(filteredRowsIds);
        dispatch(loadParentIdsState(filteredRowsIds));
        setRowsToListInGrid([...filteredRows]);
    }, [rowData, dispatch]);
    useEffect(() => {
        return () => {
            dispatch(loadCompareEnabledState(false));
        };
    }, [dispatch]);

    const defaultColDef = useMemo(
        () => ({
            resizable: true,
            suppressMovable: false,
            wrapText: true,
            autoHeight: true,
        }),
        []
    );

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const getFilterQueryString = () => {
        let queryString = productCategoryId ? `product_category=${productCategoryId}` : "";
        if (Object.keys(studyPageFilters).length > 0 && studyPageFilters && Object.keys(studyPageFilters).length > 0) {
            Object.keys(studyPageFilters).forEach((filter) => {
                let checkedValue = "";
                let _filter = filter;
                studyPageFilters[_filter].forEach((datum) => {
                    if (datum.checked) {
                        if (checkedValue !== "") {
                            checkedValue += ",";
                        }
                        if (_filter === "studyPeriod") {
                            checkedValue += datum.label;
                        } else {
                            checkedValue += datum.value;
                        }
                    }
                });
                if (checkedValue !== "") {
                    if (queryString !== "") {
                        queryString += "&";
                    }

                    if (_filter === "studyType") {
                        _filter = "study_type";
                    } else if (_filter === "studyTypeMethod") {
                        _filter = "study_type_method";
                    } else if (_filter === "deodorantFormat") {
                        _filter = "deodorant_format";
                    } else if (_filter === "studyPeriod") {
                        _filter = "study_period__title";
                    } else if (_filter === "unileverBrand") {
                        _filter = "unilever_brand";
                    } else if (_filter === "country") {
                        _filter = "country";
                    } else {
                        _filter = "";
                    }

                    queryString += _filter + "=" + checkedValue;
                }
            });
        }
        return queryString;
    };

    useEffect(() => {
        const productAIds = matchNetSelectedProducts?.map((product) => product.product_a_id).join(",");
        if (productAIds && algorithm) {
            const filterQueryString = getFilterQueryString();
            const url = process.env.REACT_APP_SERVER_PATH + `/deodrants-match-net-analysis/lookup/?archive=false&parsed=true&${filterQueryString}&algorithm=${algorithm}&product_a=${productAIds}`;
            setIsLoading(true);
            try {
                api.get(url)
                    .then(async (res) => {
                        const productsData = res?.data || [];
                        setRowData(productsData);
                        dispatch(loadLookupProductsState(productsData || []));
                        setIsLoading(false);
                    })
                    .catch(async (error) => {
                        handleError("error", error);
                        setIsLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsLoading(false);
            }
        }
    }, []);

    const onSelectionChanged = useCallback(() => {
        const _selectedRows = gridRef.current.api.getSelectedRows() || [];
        setSelectedProductRows(_selectedRows);
        dispatch(loadLookupSelectedProductsState(_selectedRows));
        const hasParent = _selectedRows.some((row) => row.isParentRow);
        const hasChild = _selectedRows.some((row) => !row.isParentRow);
        dispatch(loadCompareEnabledState(hasParent && hasChild));
    }, [dispatch]);

    useEffect(() => {
        const selectedParentIds =
            selectedProductRows?.filter((row) => row.isParentRow).map((row) => row.id) || [];

        let newCheckboxSelectionIds = [];

        if (selectedParentIds.length > 0) {
            const selectedParentId = selectedParentIds[0];

            const childIds = rowData
                .filter((item) => item.parent.id === selectedParentId)
                .flatMap((item) => item.children.map((child) => `${item.parent.id}-${child.id}`));

            newCheckboxSelectionIds = [selectedParentId, ...childIds];
        } else {
            newCheckboxSelectionIds = [...parentRowIds];
        }

        setCheckboxSelectionIds(newCheckboxSelectionIds);

        gridRef.current?.api?.forEachNode((node) => {
            if (node.isSelected() && node.data?.parentId && !selectedParentIds.includes(node.data.parentId)) {
                node.setSelected(false);
            }
        });
        gridRef.current?.api?.refreshCells({ columns: ["title"], force: true });
    }, [selectedProductRows, rowData, parentRowIds]);

    const getRowClass = (params) => {
        const classes = ["matchnet-group-row"];
        if (params.data?.isParentRow) {
            classes.push("matchnet-group-start");
        }
        if (params.data?.isGroupEnd) {
            classes.push("matchnet-group-end");
        }
        return classes;
    };

    const getRowStyle = (params) => {
        if (params.data?.isParentRow) {
            return {
                backgroundColor: "#DDE7FF",
                textAlign: "center",
                fontWeight: 600,
            };
        }

        return {
            backgroundColor: "white",
            textAlign: "center",
        };
    };

    return (
        <>
            {isLoading && <Loader />}
            <div
                className="ag-theme-alpine matchnet-lookup-grid"
                style={{
                    height: "calc(100vh - 252px)",
                    width: "100%",
                    position: "relative",
                }}
            >
                <style>
                    {`
                        .ag-header-cell-label {
                            justify-content: center !important;
                            text-align: center !important;
                        }
                        .ag-header-cell-text {
                            text-align: center !important;
                            padding-bottom: 2px !important;
                        }
                        .ag-header-group-cell-label {
                            justify-content: center !important;
                        }
                        .ag-header-cell-comp-wrapper {
                            justify-content: center !important;
                        }
                        .matchnet-lookup-grid .ag-root-wrapper {
                        border: none;
                        }
                        .ag-row-selected::before {
                            background-color: transparent !important;
                        }
                        .ag-row.matchnet-group-row::after {
                            content: "";
                            position: absolute;
                            top: 0;
                            bottom: 0;
                            left: 4px;
                            right: 4px;
                            pointer-events: none;
                            z-index: 2;
                            border-left: 2px solid #005eef80;
                            border-right: 2px solid #005eef80;
                        }
                        .ag-row.matchnet-group-start::after {
                            top: 8px;
                            z-index: 0;
                            background-color: #DDE7FF;
                            border-top: 2px solid #005eef80;
                            border-top-left-radius: 8px;
                            border-top-right-radius: 8px;
                        }
                        .ag-row.matchnet-group-start .ag-cell {
                            z-index: 1;
                        }
                        .ag-row.matchnet-group-row {
                            clip-path: inset(0 4px 0 4px);
                        }
                        .ag-row.matchnet-group-start {
                            clip-path: inset(8px 4px 0 4px round 8px 8px 0 0);
                        }
                        .ag-row.matchnet-group-end {
                            clip-path: inset(0 4px 4px 4px round 0 0 8px 8px);
                        }
                        .ag-row.matchnet-group-start.matchnet-group-end {
                            clip-path: inset(8px 4px 4px 4px round 8px);
                        }
                        .ag-row.matchnet-group-end::after {
                            bottom: 4px;
                            border-bottom: 2px solid #005eef80;
                            border-bottom-left-radius: 8px;
                            border-bottom-right-radius: 8px;
                        }
                        .ag-row.matchnet-group-start .ag-cell {
                            padding-top: 12px;
                        }
                    `}
                </style>
                <AgGridReact
                    ref={gridRef}
                    getRowId={(params) =>
                        params.data.isParentRow
                            ? `parent-${params.data.id}`
                            : `child-${params.data.parentId}-${params.data.id}`
                    }
                    rowSelection="multiple"
                    rowMultiSelectWithClick={true}
                    rowData={rowsToListInGrid}
                    columnDefs={columns}
                    defaultColDef={defaultColDef}
                    getRowStyle={getRowStyle}
                    getRowClass={getRowClass}
                    onSelectionChanged={onSelectionChanged}
                    rowHeight={30}
                    headerHeight={60}
                    floatingFiltersHeight={40}
                    suppressDragLeaveHidesColumns={true}
                    suppressRowClickSelection={true}
                    suppressRowHoverHighlight={true}
                    suppressCellFocus={true}
                    animateRows={true}
                />
            </div>
            <p
                style={{
                    marginTop: "20px",
                    marginLeft: "4px",
                    color: "#495057",
                    fontWeight: 600,
                    fontSize: 12,
                }}
            >
                Select a <span style={{ color: "#005eef" }}>Parent Product</span> first, Compatible options unlock after selection.
            </p>
        </>
    );
};

export default MatchNetLookupTable;
 